import { useCallback, useState } from 'react'
import { useParams } from 'react-router'
import { useAppSelector } from 'common/hooks/redux'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { CanvasCaptureRef } from 'features/Procedure/procedure-model/canvas-capture'
import { useSceneActions } from 'features/Procedure/hooks/useSceneActions'
import { useFetchSoapNotesQuery } from 'features/Procedure/state/procedure.api'
import { hasPendingSoapImages } from 'features/Procedure/state/soapImageUpload.helpers'
import { useUploadProcedureSoapImage } from './useUploadProcedureSoapImage'

type Props = {
  canvasCaptureRef: React.RefObject<CanvasCaptureRef> | null
  selectedPatientId: number | null
  selectedEncounterUid: string | null
}

export const useCaptureNoteImage = ({
  canvasCaptureRef,
  selectedPatientId,
  selectedEncounterUid,
}: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { setNotification } = useNotification()
  const [isCapturing, setIsCapturing] = useState(false)
  const { handleSetShowElementIds, handleSetIsPrinting, handleSetDrawingElements } =
    useSceneActions()

  const selectedProcedure = useAppSelector((state) => state.procedureReducer.selectedProcedure)
  const showElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
  const drawingElements = useAppSelector((state) => state.procedureReducer.drawingElements)
  const isProcedureDeletePending = useAppSelector(
    (state) => state.procedureReducer.isProcedureDeletePending,
  )

  const { uploadImage } = useUploadProcedureSoapImage({
    siteId,
    selectedPatientId,
    selectedEncounterUid,
  })

  const { currentData: soapNotesData } = useFetchSoapNotesQuery(
    { patientPid: selectedPatientId!, encounterUid: selectedEncounterUid! },
    { skip: !selectedPatientId || !selectedEncounterUid },
  )

  const canCapture = Boolean(
    siteId &&
      selectedPatientId &&
      selectedEncounterUid &&
      selectedProcedure &&
      !isCapturing &&
      !hasPendingSoapImages(soapNotesData) &&
      !isProcedureDeletePending,
  )

  const captureImage = useCallback(async () => {
    if (!canCapture) return
    if (
      !canvasCaptureRef?.current ||
      !siteId ||
      !selectedPatientId ||
      !selectedEncounterUid ||
      !selectedProcedure
    ) {
      return
    }

    setIsCapturing(true)
    const originalShowElementIds = showElementIds
    const originalDrawingElements = drawingElements
    const currentProcedureElements = drawingElements.filter(
      (el) => el.procedureId === selectedProcedure.id,
    )

    handleSetShowElementIds(true)
    handleSetIsPrinting(true)
    handleSetDrawingElements(currentProcedureElements)

    await new Promise((resolve) => setTimeout(resolve, 100))
    await new Promise((resolve) => requestAnimationFrame(resolve))

    let imageData: string | null = null

    try {
      try {
        imageData = await canvasCaptureRef.current.captureCanvas()
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Unable to capture image',
          description: error?.data?.message || 'Please try again later.',
        })
      } finally {
        handleSetDrawingElements(originalDrawingElements)
        handleSetShowElementIds(originalShowElementIds)
        handleSetIsPrinting(false)
      }

      if (!imageData) {
        return
      }

      await uploadImage({ procedure: selectedProcedure, imageDataUrl: imageData })
    } finally {
      setIsCapturing(false)
    }
  }, [
    canCapture,
    canvasCaptureRef,
    siteId,
    selectedPatientId,
    selectedEncounterUid,
    selectedProcedure,
    showElementIds,
    drawingElements,
    handleSetShowElementIds,
    handleSetIsPrinting,
    handleSetDrawingElements,
    setNotification,
    uploadImage,
  ])

  return {
    captureImage,
    isCapturing,
    canCapture,
  }
}
